import {OFetchError, useLogger, useRoute} from '#imports';
import {safelyNavigateTo} from '@malt/utils';
import type {TJoySnackbarLevels} from '@maltjoy/core-vue';
import type {FetchError} from '@api';
import {useRestrictionsStore} from '~/features/keyword-search/stores/restrictions.store';
import {useSearchUrlStore} from '~/features/keyword-search/stores/search-url.store';
import {useGenericErrorHandler} from './useGenericErrorHandler';

export function useErrorHandler() {
    const logger = useLogger();
    const route = useRoute();
    const searchUrlStore = useSearchUrlStore();
    const restrictionsStore = useRestrictionsStore();
    const {handleError} = useGenericErrorHandler();

    function handlePoolOfFreelancesApiError(error: FetchError, customErrorMsg?: string, level?: TJoySnackbarLevels) {
        if (error.statusCode === 401 || error.statusCode === 403) {
            logger.debug('User must signup as client to use pool of freelance');
            restrictionsStore.redirectExternal({
                path: '/signup-client',
                query: {
                    redirect: route.fullPath,
                },
            });
        } else {
            handleError(error, customErrorMsg, level);
        }
    }

    function handleSearchProfilesApiError(error: FetchError, customErrorMsg?: string, level?: TJoySnackbarLevels) {
        if (error.statusCode === 401) {
            logger.debug('User must signup to execute more searches');
            const redirectUrl = `${route.path}?${searchUrlStore.toUrlQueryParams()}`;
            restrictionsStore.redirectExternal({
                path: '/who-are-you',
                query: {
                    redirect: redirectUrl,
                },
            });
        } else if (error.statusCode === 403) {
            logger.debug('User has abused search engine');
            restrictionsStore.redirectExternal({
                path: '/client-settings/restricted',
            });
        } else if (error.statusCode === 429) {
            logger.debug('User has performed too many searches');
            restrictionsStore.userPerformedTooMuchSearch();
        } else {
            handleError(error, customErrorMsg, level);
        }
    }

    function handleSubmitBriefError(error: FetchError) {
        const redirectUrl = error.cause instanceof OFetchError ? error.cause.response?.headers?.get('location') : undefined;
        if (redirectUrl) {
            logger.debug('User must be redirected to another page');
            safelyNavigateTo(redirectUrl, {externalToThisApp: true});
        } else {
            handleError(error);
        }
    }

    return {
        handlePoolOfFreelancesApiError,
        handleSearchProfilesApiError,
        handleSubmitBriefError,
    };
}
